import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  UtensilsCrossed, Bath, Sofa, Home, Hammer, CheckCircle, ArrowRight, 
  Phone, Shield, ThumbsUp, Award, Star, Paintbrush, DollarSign
} from 'lucide-react';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const RemodelingPage = () => {
  const transformations = [
    {
      title: 'Kitchen',
      before: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/7fmepb74_IMG_3932.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/1xv4pktt_IMG_3501.jpeg',
      description: 'Complete kitchen renovation featuring custom cabinetry, quartz countertops, a spacious center island, and modern fixtures throughout.'
    },
    {
      title: 'Bathroom',
      before: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/a0ktaccq_IMG_4443.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/zmojaqx6_IMG_3566.jpeg',
      description: 'Full bathroom remodel with frameless glass shower, contemporary tile work, and updated plumbing fixtures for a clean, modern look.'
    },
    {
      title: 'Whole Home',
      before: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/8p00ka9e_IMG_4478.jpeg',
      after: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/9jid320m_IMG_9012.jpeg',
      description: 'Complete restoration after fire damage — rebuilt from the ground up with new siding, windows, roofing, and a custom deck addition.'
    }
  ];

  const services = [
    {
      icon: UtensilsCrossed,
      title: 'Kitchen Remodeling',
      description: 'Transform your kitchen into the heart of your home with custom cabinetry, modern appliances, and functional layouts.',
      image: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/9cs6ho7a_IMG_3603.jpeg'
    },
    {
      icon: Bath,
      title: 'Bathroom Renovations',
      description: 'Update your bathroom with modern fixtures, improved layouts, and quality materials that fit your style and budget.',
      image: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/tn6zuzjt_IMG_6493.jpeg'
    },
    {
      icon: Sofa,
      title: 'Basement Finishing',
      description: 'Turn unused space into a functional living area, home office, or entertainment room.',
      image: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/ron43umo_IMG_1509.jpeg'
    },
    {
      icon: Home,
      title: 'Full Home Renovations',
      description: 'Comprehensive remodeling services to transform your entire home, inside and out.',
      image: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/j697k33r_IMG_4056.jpeg'
    },
    {
      icon: Hammer,
      title: 'Custom Carpentry',
      description: 'Built-in shelving, custom closets, and fine finish carpentry to elevate your space.',
      image: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/xiiehzb2_IMG_2326.jpeg'
    },
    {
      icon: Paintbrush,
      title: 'Exterior & Decks',
      description: 'Siding, roofing, decks, and outdoor living spaces to enhance your home\'s curb appeal.',
      image: 'https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/9jid320m_IMG_9012.jpeg'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We meet to discuss your vision, budget, and timeline. We\'ll assess your space and provide expert recommendations.'
    },
    {
      step: '02',
      title: 'Design & Planning',
      description: 'Our team creates detailed plans and 3D renderings. We handle all permits and coordinate with architects as needed.'
    },
    {
      step: '03',
      title: 'Construction',
      description: 'Expert craftsmen bring your vision to life with quality materials and meticulous attention to detail.'
    },
    {
      step: '04',
      title: 'Final Walkthrough',
      description: 'We ensure every detail meets your expectations. Your satisfaction is our priority.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section - Premium Style */}
      <section className="relative py-20 md:py-24 bg-[#003366] text-white overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_32fc87a5-deb6-4ab4-9d27-c204526bbdbf/artifacts/xd4k05bj_IMG_7830.jpeg)'
          }}
        />
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        {/* Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img src="https://customer-assets.emergentagent.com/job_waitlist-system-5/artifacts/0d36dd51_Transparent%20Png-02.png" alt="" className="w-[400px] md:w-[500px] h-auto opacity-[0.06]" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mb-6">
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-2">
              Transform Your Home with
            </span>
            <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
              <span className="text-[#CFB973]">Valenrock<sup className="text-[0.5em]">™</sup> Remodelers</span>
            </span>
          </h1>
          
          {/* Decorative line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
            <div className="w-2 h-2 bg-[#CFB973] rotate-45"></div>
            <div className="w-12 h-0.5 bg-[#CFB973]"></div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            Expert remodeling and construction services from the team you trust. <span className="text-[#CFB973] font-semibold">Quality craftsmanship</span>, transparent pricing, and exceptional results.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-7 text-lg shadow-xl">
                Get a Project Estimate
              </Button>
            </Link>
            <a href="tel:5513799774">
              <Button size="lg" className="bg-transparent hover:bg-white/10 text-white font-semibold px-10 py-7 text-lg border-2 border-[#CFB973]">
                <Phone className="mr-2" size={20} />
                (551) 379-9774
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-[#E8EEF3]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-8">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Shield className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Licensed & Insured</p>
              <p className="text-[#003366]/60 text-xs">#13VH13590600</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <ThumbsUp className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">100% Satisfaction</p>
              <p className="text-[#003366]/60 text-xs">Guarantee</p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <Award className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">10+ Years</p>
              <p className="text-[#003366]/60 text-xs">Experience</p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-[#CFB973] flex items-center justify-center mb-3 shadow-lg">
                <DollarSign className="w-10 h-10 text-[#003366]" />
              </div>
              <p className="text-[#003366] font-bold text-sm text-center">Financing</p>
              <p className="text-[#003366]/60 text-xs">Available</p>
            </div>
          </div>
          
          {/* EPA Certification - Standalone */}
          <div className="flex flex-col items-center pt-6 border-t border-[#003366]/10">
            <img 
              src="https://customer-assets.emergentagent.com/job_smarthouse-staging/artifacts/inesgu3y_IMG_7280.png" 
              alt="EPA Lead-Safe Certified Firm" 
              className="h-24 md:h-28 w-auto object-contain mb-2 mix-blend-multiply"
            />
            <p className="text-[#003366] font-bold text-sm">EPA Lead-Safe Certified Firm</p>
            <p className="text-[#003366]/60 text-xs">NAT-F326598-1</p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Remodeling Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From kitchens to full home renovations, we handle projects of all sizes with the same commitment to quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 border-gray-100 hover:border-[#CFB973] transition-all duration-300 overflow-hidden hover:shadow-xl group">
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#003366]/30 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center shadow-lg">
                        <Icon className="text-[#003366]" size={24} />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#003366] mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Financing Callout */}
          <div className="mt-12 bg-[#003366] rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <DollarSign className="w-8 h-8 text-[#CFB973]" />
              <h3 className="text-2xl font-bold text-white">Financing Available</h3>
            </div>
            <p className="text-white/80 max-w-2xl mx-auto">
              Don't let budget hold back your dream renovation. We offer flexible financing options for all remodeling projects. Ask us about payment plans during your consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Before/After Transformations Section */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              See Our Transformations
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Drag the slider to reveal the dramatic before and after results of our work.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {transformations.map((item, index) => (
              <BeforeAfterSlider
                key={index}
                title={item.title}
                beforeImage={item.before}
                afterImage={item.after}
                beforeLabel="Before"
                afterLabel="After"
                description={item.description}
                darkMode={true}
              />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/remodeling/portfolio">
              <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-10 py-6 shadow-xl">
                Explore Full Portfolio
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#E8EEF3]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Our Remodeling Process
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A transparent, structured approach that keeps you informed every step of the way.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative bg-white rounded-2xl p-8 shadow-lg">
                <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-6 shadow-lg">
                  <span className="text-[#003366] font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-[#003366] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="text-[#CFB973]" size={28} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-8">
                Why Choose Valenrock<sup className="text-[0.5em]">™</sup> Remodelers?
              </h2>
              <div className="space-y-5">
                {[
                  'Licensed & bonded professionals (License #13VH13590600)',
                  'Transparent pricing with detailed estimates',
                  'Quality materials and expert craftsmanship',
                  'Project managers who keep you informed',
                  'Clean, respectful work sites',
                  'Warranty on all work completed',
                  'Same team, same values as Valenrock™ Home Services'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#CFB973] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-[#003366]" />
                    </div>
                    <span className="text-gray-700 text-lg">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#003366] rounded-2xl p-8 shadow-2xl relative overflow-hidden">
              {/* Blueprint pattern */}
              <div className="absolute inset-0 opacity-[0.05]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
                backgroundSize: '100px 100px'
              }}></div>
              
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-full bg-[#CFB973] flex items-center justify-center mb-6 shadow-lg">
                  <Home className="w-8 h-8 text-[#003366]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Already Have a Home Profile?
                </h3>
                <p className="text-white/80 mb-6">
                  If you've completed a Whole-Home Assessment, your Home Profile gives us a head start. We already know your property, your priorities, and your style.
                </p>
                <p className="text-white/80 mb-8">
                  From routine maintenance to complete renovations — one trusted partner for every stage of homeownership.
                </p>
                <Link to="/contact">
                  <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-8 py-6 shadow-lg">
                    Discuss Your Project
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-10 shadow-xl text-center">
            <div className="flex justify-center mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="text-[#CFB973] fill-[#CFB973]" size={24} />)}
            </div>
            <p className="text-xl md:text-2xl text-gray-700 italic mb-6">
              "Valenrock™ transformed our outdated kitchen into a modern masterpiece. From the first consultation to the final walkthrough, they were professional, communicative, and delivered beyond our expectations."
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#003366] flex items-center justify-center text-white font-bold text-xl">
                MK
              </div>
              <div className="text-left">
                <p className="font-bold text-[#003366]">Michael K.</p>
                <p className="text-gray-500 text-sm">Kitchen Remodel, Montclair</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#003366] relative overflow-hidden">
        {/* Blueprint pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Remodeling Project?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Complete our intake form to get started. We'll review your project details and schedule a consultation.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#CFB973] hover:bg-[#CFB973]/90 text-[#003366] font-bold px-12 py-7 text-xl shadow-xl">
              Complete Intake Form
            </Button>
          </Link>
          <p className="text-white/60 text-sm mt-6">
            Get a detailed estimate and project timeline within 48 hours
          </p>
          
          {/* Contact Info */}
          <div className="mt-10 pt-8 border-t border-white/20 flex flex-col sm:flex-row justify-center items-center gap-6 text-white/70">
            <a href="tel:5513799774" className="flex items-center gap-2 hover:text-[#CFB973] transition-colors">
              <Phone size={18} />
              <span>(551) 379-9774</span>
            </a>
            <span className="hidden sm:block">•</span>
            <span>contact@valenrockhome.services</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RemodelingPage;
